/**
 * Traffic Light Grid - 7 dimension assessment display.
 *
 * Displays ratings for: Drive & Ambition, Resilience, People & Culture,
 * Risk Discipline, Intellectual Range, Derailer Flags, and Motivations.
 */

import { DimensionAssessment, ExecutiveDimension, DIMENSION_LABELS, RATING_COLORS, DimensionRating } from './types';

interface TrafficLightGridProps {
  dimensions?: DimensionAssessment[];
  compact?: boolean;
}

// All dimensions in display order
const DIMENSION_ORDER: ExecutiveDimension[] = [
  'drive_ambition',
  'resilience',
  'people_culture',
  'risk_discipline',
  'intellectual_range',
  'derailer_flags',
  'motivations',
];

function RatingIndicator({ rating, size = 'md' }: { rating: DimensionRating; size?: 'sm' | 'md' | 'lg' }) {
  const colors = RATING_COLORS[rating];
  const sizeClasses = {
    sm: 'h-4 w-4 text-xs',
    md: 'h-6 w-6 text-sm',
    lg: 'h-8 w-8 text-base',
  };

  return (
    <div
      className={`${sizeClasses[size]} rounded-full ${colors.bg} flex items-center justify-center`}
      title={rating === 'insufficient' ? 'Insufficient Data' : rating.charAt(0).toUpperCase() + rating.slice(1)}
    >
      <span>{colors.icon}</span>
    </div>
  );
}

function DimensionRow({
  dimension,
  assessment,
  compact,
}: {
  dimension: ExecutiveDimension;
  assessment?: DimensionAssessment;
  compact?: boolean;
}) {
  const meta = DIMENSION_LABELS[dimension];
  const rating = assessment?.rating || 'insufficient';
  const colors = RATING_COLORS[rating];

  if (compact) {
    return (
      <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900">{meta.label}</span>
        </div>
        <RatingIndicator rating={rating} size="sm" />
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${colors.bg} border-gray-200 p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">{meta.label}</span>
            <RatingIndicator rating={rating} size="sm" />
          </div>
          <p className="text-xs text-gray-500 mt-1">{meta.description}</p>
          {assessment?.rationale && (
            <p className={`text-sm mt-2 ${colors.text}`}>
              {assessment.rationale}
            </p>
          )}
          {assessment?.evidence && assessment.evidence.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-500 mb-1">Evidence:</p>
              <ul className="text-xs text-gray-600 space-y-0.5">
                {assessment.evidence.slice(0, 3).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-1">
                    <span className="text-gray-400">-</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function TrafficLightGrid({ dimensions = [], compact = false }: TrafficLightGridProps) {
  // Create a map of dimension -> assessment
  const assessmentMap = new Map<ExecutiveDimension, DimensionAssessment>();
  dimensions.forEach(d => assessmentMap.set(d.dimension, d));

  // Check if we have any assessments
  const hasAssessments = dimensions.length > 0;

  if (!hasAssessments && compact) {
    return (
      <div className="text-center py-4 text-sm text-gray-500">
        Assessment pending
      </div>
    );
  }

  if (compact) {
    return (
      <div className="divide-y divide-gray-100">
        {DIMENSION_ORDER.map(dim => (
          <DimensionRow
            key={dim}
            dimension={dim}
            assessment={assessmentMap.get(dim)}
            compact
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Executive Assessment</h3>
        {!hasAssessments && (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
            Insufficient Data
          </span>
        )}
      </div>

      {!hasAssessments ? (
        <div className="rounded-lg border-2 border-dashed border-gray-200 p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h4 className="mt-2 text-sm font-medium text-gray-900">Assessment Not Available</h4>
          <p className="mt-1 text-sm text-gray-500">
            Gather more data to generate traffic light ratings for this executive.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {DIMENSION_ORDER.map(dim => (
            <DimensionRow
              key={dim}
              dimension={dim}
              assessment={assessmentMap.get(dim)}
            />
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <RatingIndicator rating="green" size="sm" />
          <span>Positive</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <RatingIndicator rating="yellow" size="sm" />
          <span>Mixed/Neutral</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <RatingIndicator rating="red" size="sm" />
          <span>Concern</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <RatingIndicator rating="insufficient" size="sm" />
          <span>Insufficient Data</span>
        </div>
      </div>
    </div>
  );
}

export default TrafficLightGrid;
